import {
  TOPIC_CONTENT_SOURCES,
  type TopicContentSource,
} from "./topic-content-sources";
import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const EMPLOYER_SPONSORED_VISA_GUIDE = {
  topicId: "employer-sponsored-visa",
  locale: "ko",
  publicationStatus: "korean_test_preview",
  risk: "R4",
  checkedAt: "2026-07-16",
  jurisdictionLabel: "호주 전역·고용주 스폰서 비자 경로별 기준",
  summary:
    "고용주가 비자 스폰서를 제안했다면 먼저 제안서의 직무·근무지·급여·계약기간과 자신의 현재 VEVO 조건을 저장하세요. 고용주 스폰서 비자는 하나의 제도가 아니라 Skills in Demand 482, Employer Nomination Scheme 186, regional 494와 labour agreement 등 서로 다른 경로가 있으며, sponsor·nomination과 개인 visa application은 별도 심사를 받습니다. 직업명만 맞춘다고 되는 것이 아니라 실제 업무가 해당 occupation code와 일치하는지, 현재 occupation list·caveat·stream·skills·English·licensing 요건을 확인해야 합니다. 고용주가 승인이나 영주권을 보장하거나 sponsorship 대가를 요구할 수 없으며, 고용이 바뀌거나 종료되면 추측으로 다른 일을 시작하지 말고 grant notice·VEVO와 Home Affairs의 현재 변경 절차를 즉시 확인하세요.",
  keyPoints: [
    "482·186·494는 목적, 임시·영주 상태, stream, 지역 조건과 신청 요건이 서로 다릅니다.",
    "sponsor approval, position nomination과 worker visa application은 구분해 확인하세요.",
    "직책명이 아니라 실제 duties, ANZSCO code, occupation list와 caveat가 맞는지 검토하세요.",
    "급여는 visa 기준뿐 아니라 market salary, award·agreement와 실제 근로조건을 함께 확인해야 합니다.",
    "skills assessment, 경력, English, health, character와 licence가 경로별로 필요할 수 있습니다.",
    "고용주가 sponsorship 또는 일자리 대가로 돈이나 임금 반환을 요구하는 것은 불법일 수 있습니다.",
    "고용주가 비자를 취소할 수 없으며 visa grant·refusal·cancellation 권한은 Home Affairs에 있습니다.",
    "고용주·직무·근무지·고용상태가 바뀌면 현재 visa 조건과 새 nomination 필요 여부를 즉시 확인하세요.",
    "temporary sponsored visa가 자동으로 permanent residence를 보장하지 않습니다.",
  ],
  sections: [
    {
      id: "identify-pathway-and-status",
      title: "1. 제안받은 정확한 visa pathway와 현재 체류조건을 확인하세요",
      body: [
        "고용주에게 단순히 ‘스폰서 비자’가 아니라 제안하는 subclass와 stream, sponsor·nomination 진행 상태, nominated occupation, 근무지, 고용기간과 예상 신청 순서를 서면으로 요청하세요. 482는 temporary employer-sponsored pathway이고, 186은 permanent employer nomination pathway이며, 494는 designated regional area의 provisional pathway입니다. labour agreement를 이용하는 제안이라면 agreement 이름과 적용 concession도 별도로 확인하세요.",
        "자신의 현재 visa grant notice와 VEVO에서 expiry, work condition, employer·occupation 제한과 bridging visa 상태를 확인하세요. 새 제안이나 nomination이 접수됐다는 사실만으로 현재 visa 조건이 바뀌거나 다른 고용주에게 일할 권리가 생긴다고 가정하지 마세요. offshore·onshore, substantive visa·bridging visa와 이전 refusal·cancellation 이력이 있으면 신청 가능성과 timing이 크게 달라질 수 있으므로 registered migration agent 또는 Australian legal practitioner에게 확인하세요.",
      ],
      checklist: [
        "현재 passport, visa grant notice와 VEVO 결과",
        "제안받은 subclass·stream·labour agreement 이름",
        "sponsor approval·nomination·visa application의 진행 단계",
        "nominated occupation code와 실제 position title",
        "근무지·regional 여부·고용기간과 시작 조건",
        "현재 visa expiry와 bridging visa 가능성",
      ],
      sourceIds: [
        "employer-sponsored-source-sid-482",
        "employer-sponsored-source-ens-186",
        "employer-sponsored-source-regional-494",
        "employer-sponsored-source-sponsoring",
      ],
    },
    {
      id: "verify-position-and-nomination",
      title: "2. occupation·duties·급여와 nomination 조건을 대조하세요",
      body: [
        "Home Affairs의 current skilled occupation list에서 해당 visa와 stream에 occupation이 사용 가능한지, 어떤 ANZSCO version과 code를 쓰는지, caveat와 assessing authority가 무엇인지 확인하세요. 비슷한 직책을 고르는 것이 아니라 position description, 일상 duties, required skill level과 실제 조직 구조가 code 설명과 일치해야 합니다. occupation이 나중에 변경되거나 employer·duties가 바뀌면 새 nomination에 영향을 줄 수 있습니다.",
        "고용계약에서 base salary, super, allowance, hours, location, duties, probation, termination과 repayment 조항을 확인하세요. visa salary threshold나 market salary assessment의 현재 금액을 기억에 의존하지 말고 nomination 시점 Home Affairs 안내에서 재확인해야 합니다. 동시에 Fair Work award·enterprise agreement·National Employment Standards와 같은 직무의 Australian worker에게 적용되는 pay·conditions를 비교하세요. nomination에 표시된 역할과 실제 근무가 다르거나 일부 임금을 돌려주라는 요구가 있으면 증거를 보관하고 독립적인 도움을 받으세요.",
      ],
      checklist: [
        "current occupation list·ANZSCO version·code",
        "occupation caveat와 skills assessing authority",
        "position description과 실제 핵심 duties",
        "base salary·super·allowance·hours·location",
        "market salary·visa salary requirement의 현재 근거",
        "award·agreement·NES와 같은 직무의 조건",
        "repayment·deduction·sponsorship fee 조항",
      ],
      sourceIds: [
        "employer-sponsored-source-occupation-list",
        "employer-sponsored-source-sid-482",
        "employer-sponsored-source-ens-186",
        "employer-sponsored-source-worker-rights",
      ],
    },
    {
      id: "prepare-worker-evidence",
      title: "3. 경력·자격·English·licence와 신원자료를 사실대로 준비하세요",
      body: [
        "passport, qualification, transcript, employment reference, payslip·tax·super·bank records와 position evidence를 날짜순으로 정리하세요. reference letter의 title만으로 경력을 과장하지 말고 실제 duties, 기간, hours와 employer contact를 다른 기록과 일치시키세요. skills assessment가 required인지, mandatory licensing·registration이 있는 직업인지, qualification·experience 조합이 stream 요건을 충족하는지는 신청 시점 공식 안내에서 확인합니다.",
        "English, health examination, police certificate와 family member documents의 requirement와 validity는 subclass·stream·passport·신청 위치에 따라 달라질 수 있습니다. 오래된 test score나 police check를 먼저 구매하기 전에 current document checklist를 확인하세요. 문서를 조작하거나 employer가 작성해 준 사실과 다른 reference를 제출하면 visa뿐 아니라 향후 신청에도 중대한 영향을 줄 수 있습니다. application에 답하지 못한 질문이나 과거 refusal·cancellation·criminal matter가 있다면 lodging 전에 전문가에게 공개하고 조언을 받으세요.",
      ],
      checklist: [
        "passport·name change·family identity documents",
        "qualification·transcript·licence·registration",
        "employment references와 duties·hours·dates",
        "payslip·tax·super·bank 등 경력 보조증거",
        "skills assessment 필요 여부와 유효기간",
        "English·health·character의 current requirement",
        "과거 visa history와 설명이 필요한 불일치",
      ],
      sourceIds: [
        "employer-sponsored-source-sid-482",
        "employer-sponsored-source-ens-186",
        "employer-sponsored-source-regional-494",
        "employer-sponsored-source-occupation-list",
      ],
    },
    {
      id: "lodge-and-protect-rights",
      title: "4. 신청 기록을 분리 보관하고 sponsorship 대가·착취에 대응하세요",
      body: [
        "고용주의 sponsor·nomination receipt와 자신의 ImmiAccount application·TRN·제출문서·결제기록을 각각 보관하세요. migration agent를 사용하면 등록 상태, written service agreement, fee와 누가 어떤 application을 관리하는지 확인하세요. employer가 모든 계정을 통제하게 두지 말고 본인 visa application과 Home Affairs correspondence에 접근할 수 있어야 합니다. 추가자료 요청과 health·biometrics·police document deadline은 ImmiAccount에서 직접 확인하세요.",
        "visa sponsorship 또는 sponsorship이 필요한 일자리 대가로 고용주나 intermediary에게 돈을 지급하거나 급여 일부를 돌려주는 방식은 불법일 수 있습니다. visa holder도 다른 직원과 같은 기본 workplace rights를 가지며, 고용주가 passport를 보관하거나 visa cancellation을 위협해서는 안 됩니다. payslip, roster, hours, bank deposit와 employer message를 안전한 개인 계정에 보관하세요. 착취가 의심되면 Fair Work Ombudsman, union, community legal centre 또는 lawyer에게 도움을 요청할 수 있고 employer는 비자를 취소할 권한이 없습니다.",
      ],
      checklist: [
        "sponsor·nomination receipt와 worker TRN 구분",
        "ImmiAccount 접근권한과 제출문서 사본",
        "agent registration·service agreement·invoice",
        "Home Affairs request와 response deadline",
        "contract·payslip·roster·hours·bank deposit",
        "passport 원본과 개인 연락·email 접근권한",
        "sponsorship payment·cashback·협박 증거",
      ],
      sourceIds: [
        "employer-sponsored-source-sponsoring",
        "employer-sponsored-source-fair-work",
        "employer-sponsored-source-worker-rights",
      ],
    },
    {
      id: "handle-change-or-end",
      title: "5. 고용주·직무·근무지 변경이나 고용종료를 즉시 확인하세요",
      body: [
        "고용주가 바뀌거나 business가 매각·폐업하고, duties·occupation·work location·hours 또는 salary가 달라지거나 employment가 종료되면 변화 날짜와 서면 통지를 보관하세요. 현 visa의 condition, grace period, 새 sponsor·nomination 또는 새 visa가 필요한지에 관한 규칙은 변경될 수 있으므로 오래된 블로그나 이전 457·TSS 규칙을 적용하지 마세요. VEVO와 grant notice를 확인하고 Home Affairs 또는 등록 전문가에게 현재 선택지와 deadline을 즉시 확인한 뒤 다른 고용을 시작하세요.",
        "고용종료가 underpayment, discrimination, coercion 또는 sponsorship 대가 요구와 관련돼도 workplace entitlement는 사라지지 않습니다. employer가 visa를 취소한다고 협박해도 cancellation 권한은 Home Affairs에 있습니다. 동시에 workplace complaint를 했다는 사실만으로 모든 visa breach가 자동 면제되는 것은 아니므로 reporting protection·Workplace Justice visa pilot의 현재 요건과 자신의 상황을 전문적으로 확인하세요. 186 또는 다른 permanent pathway를 약속받았더라도 nomination·visa eligibility와 grant는 별도 심사이며 자동 승계되지 않습니다.",
      ],
      checklist: [
        "변경·종료 날짜와 employer written notice",
        "현재 VEVO·grant condition과 visa expiry",
        "새 sponsor·nomination·visa 필요 여부",
        "다른 일을 시작할 수 있는 정확한 시점",
        "final pay·leave·super·employment records",
        "underpayment·cashback·threat 관련 증거",
        "review·new application·reporting protection deadline",
      ],
      sourceIds: [
        "employer-sponsored-source-sid-482",
        "employer-sponsored-source-occupation-list",
        "employer-sponsored-source-fair-work",
        "employer-sponsored-source-worker-rights",
      ],
    },
  ],
  faqs: [
    {
      id: "employer-sponsored-pathway",
      question: "고용주가 스폰서해 주면 어떤 visa를 신청하나요?",
      answer:
        "하나로 정해지지 않습니다. temporary 482, permanent 186, regional provisional 494 또는 labour agreement 등 position·지역·occupation·employer와 개인 요건에 따라 경로가 다릅니다. 제안받은 subclass와 stream을 서면으로 확인하세요.",
      sourceIds: [
        "employer-sponsored-source-sid-482",
        "employer-sponsored-source-ens-186",
        "employer-sponsored-source-regional-494",
      ],
    },
    {
      id: "employer-sponsored-guarantee",
      question: "고용주가 sponsor하면 visa 승인이 보장되나요?",
      answer:
        "아닙니다. sponsor·nomination 승인과 worker visa grant는 별도 심사이며 각각의 법적 요건을 충족해야 합니다. employer나 agent가 승인 또는 처리기간을 보장할 수 없습니다.",
      sourceIds: [
        "employer-sponsored-source-sponsoring",
        "employer-sponsored-source-sid-482",
      ],
    },
    {
      id: "employer-sponsored-permanent",
      question: "482를 받으면 자동으로 186 영주권으로 이어지나요?",
      answer:
        "자동이 아닙니다. 186 stream과 nomination, employment history, age·English·skills 등 신청 시점 요건을 따로 충족해야 하며 정책과 개인 상황이 달라질 수 있습니다.",
      sourceIds: [
        "employer-sponsored-source-sid-482",
        "employer-sponsored-source-ens-186",
      ],
    },
    {
      id: "employer-sponsored-occupation-title",
      question: "회사 직책명이 occupation list와 같으면 충분한가요?",
      answer:
        "아닙니다. 실제 duties와 skill level이 해당 ANZSCO code에 맞아야 하며 visa·stream별 list와 caveat도 확인해야 합니다. title만 바꾸거나 duties를 과장해서는 안 됩니다.",
      sourceIds: ["employer-sponsored-source-occupation-list"],
    },
    {
      id: "employer-sponsored-pay-fee",
      question: "고용주가 sponsorship 비용을 제가 내야 한다고 합니다.",
      answer:
        "일부 개인 visa·agent 비용과 employer가 부담해야 할 sponsorship·nomination 관련 비용은 구분해야 합니다. sponsorship 또는 job 대가로 돈이나 임금 반환을 요구하는 것은 불법일 수 있으므로 지급 전 independent migration·employment advice와 Fair Work 도움을 받으세요.",
      sourceIds: [
        "employer-sponsored-source-fair-work",
        "employer-sponsored-source-worker-rights",
      ],
    },
    {
      id: "employer-sponsored-cancel",
      question: "고용주가 제 visa를 바로 취소할 수 있나요?",
      answer:
        "고용주가 직접 visa를 취소할 수는 없습니다. visa를 grant·refuse·cancel하는 권한은 Home Affairs에 있습니다. 다만 employment 변화가 visa condition과 status에 영향을 줄 수 있으므로 즉시 VEVO와 공식 변경 절차를 확인하세요.",
      sourceIds: [
        "employer-sponsored-source-fair-work",
        "employer-sponsored-source-worker-rights",
      ],
    },
    {
      id: "employer-sponsored-change-job",
      question: "직장을 그만두고 새 회사에서 바로 일해도 되나요?",
      answer:
        "그렇다고 가정하면 안 됩니다. 현재 visa condition, occupation, 새 sponsor·nomination과 적용되는 transition rule을 확인해야 합니다. 고용종료일을 기록하고 다른 일을 시작하기 전에 Home Affairs 또는 등록 전문가에게 현재 규칙을 확인하세요.",
      sourceIds: [
        "employer-sponsored-source-sid-482",
        "employer-sponsored-source-occupation-list",
      ],
    },
    {
      id: "employer-sponsored-underpaid",
      question: "스폰서 직원이라 award보다 적게 받아도 참아야 하나요?",
      answer:
        "아닙니다. visa holder와 migrant worker도 같은 직무의 다른 Australian employee와 같은 기본 workplace rights와 minimum entitlement를 가집니다. payslip·hours·bank records를 보관하고 Fair Work Ombudsman에 도움을 요청할 수 있습니다.",
      sourceIds: [
        "employer-sponsored-source-fair-work",
        "employer-sponsored-source-worker-rights",
      ],
    },
    {
      id: "employer-sponsored-regional",
      question: "regional employer의 제안이면 494만 가능한가요?",
      answer:
        "반드시 그렇지는 않습니다. employer location, position, occupation, agreement와 다른 visa eligibility를 비교해야 합니다. 494를 제안받았다면 designated regional area와 visa·nomination의 current requirements를 직접 확인하세요.",
      sourceIds: ["employer-sponsored-source-regional-494"],
    },
    {
      id: "employer-sponsored-agent",
      question: "회사에서 지정한 migration agent만 사용해야 하나요?",
      answer:
        "employer가 sponsor·nomination 업무에 agent를 사용할 수 있지만 worker는 자신의 visa application, advice와 documents를 이해하고 독립적인 advice를 받을 수 있어야 합니다. agent registration, client, fee와 information access를 서면으로 확인하세요.",
      sourceIds: ["employer-sponsored-source-sponsoring"],
    },
  ],
  sourceIds: [
    "employer-sponsored-source-sid-482",
    "employer-sponsored-source-ens-186",
    "employer-sponsored-source-regional-494",
    "employer-sponsored-source-occupation-list",
    "employer-sponsored-source-sponsoring",
    "employer-sponsored-source-worker-rights",
    "employer-sponsored-source-fair-work",
  ],
  additionalChecks: [
    "신청 전 해당 subclass·stream의 current eligibility, fee, processing, English·skills·age·health·character 조건을 Home Affairs에서 다시 확인하세요.",
    "nomination의 occupation code, ANZSCO version, duties, caveat, salary와 work location을 실제 contract와 대조하세요.",
    "고용변경·종료 시 다른 일을 시작하기 전 VEVO, grant notice와 current transition rule을 즉시 확인하세요.",
    "sponsorship 대가·cashback·underpayment·passport 보관이 있으면 증거를 안전하게 보관하고 Fair Work·legal support를 이용하세요.",
    "permanent pathway, regional residence와 family eligibility는 별도 심사이며 registered migration agent 또는 Australian legal practitioner에게 확인하세요.",
    "visa refusal·cancellation, unlawful status, bridging visa 또는 짧은 deadline이 있으면 즉시 specialist advice를 받으세요.",
  ],
  disclaimer:
    "이 콘텐츠는 호주 고용주 스폰서 비자에 관한 R4 테스트 미리보기 생활정보이며 migration·legal·employment advice가 아닙니다. 특정 subclass·stream의 자격, sponsor·nomination·visa 승인, 처리기간, 급여요건, 근로변경 가능 시점 또는 permanent residence 경로를 개인에게 확정하지 않습니다. 고용주 스폰서 규칙과 occupation·salary·English·skills·age·regional·transition 요건은 바뀔 수 있으므로 신청·고용변경·퇴사 전에 Home Affairs, VEVO, Fair Work Ombudsman과 등록 migration professional의 최신 안내를 확인하세요.",
} as const satisfies TopicGuide;

export const EMPLOYER_SPONSORED_VISA_SOURCES = [
  {
    sourceId: "employer-sponsored-source-sid-482",
    topicId: "employer-sponsored-visa",
    title: "Skills in Demand visa (subclass 482)",
    publisher: "Department of Home Affairs",
    url: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/skills-in-demand-visa-subclass-482",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Home Affairs 공식 visa listing의 제목·현재 URL과 2025년 5월 9일 갱신일을 확인했으나 동적 상세본문은 이 실행환경에서 제한됨",
    reviewStatus: "official_access_limited",
    claimScope:
      "Skills in Demand subclass 482의 공식 시작점, stream·nomination·worker eligibility와 고용변경 세부기준",
    followUp:
      "신청·고용변경 직전 stream, occupation, salary, work experience, English, condition와 transition rule을 Home Affairs 상세화면에서 직접 재확인",
  },
  {
    sourceId: "employer-sponsored-source-ens-186",
    topicId: "employer-sponsored-visa",
    title: "Employer Nomination Scheme (subclass 186) visa",
    publisher: "Department of Home Affairs",
    url: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/employer-nomination-scheme-186",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Home Affairs 공식 visa listing의 제목·URL과 2026년 6월 5일 갱신일을 확인했으나 stream별 상세본문은 동적 접근 제한",
    reviewStatus: "official_access_limited",
    claimScope:
      "subclass 186의 employer nomination 기반 permanent visa와 stream별 nomination·worker requirement",
    followUp:
      "TRT·Direct Entry·labour agreement stream의 current occupation, age, English, skills, employment와 nomination 요건을 lodging 전에 직접 확인",
  },
  {
    sourceId: "employer-sponsored-source-regional-494",
    topicId: "employer-sponsored-visa",
    title: "Skilled Employer Sponsored Regional (Provisional) visa (subclass 494)",
    publisher: "Department of Home Affairs",
    url: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/skilled-employer-sponsored-regional-494",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Home Affairs 공식 visa listing의 제목·URL과 공식 갱신 메타데이터를 확인했으나 regional·stream 상세본문은 동적 접근 제한",
    reviewStatus: "official_access_limited",
    claimScope:
      "subclass 494 regional provisional employer-sponsored pathway의 공식 시작점과 지역·nomination·worker requirement",
    followUp:
      "designated regional area, occupation list, regional certifying body, skills·age·English·employment와 permanent pathway 조건을 신청 시점에 재확인",
  },
  {
    sourceId: "employer-sponsored-source-occupation-list",
    topicId: "employer-sponsored-visa",
    title: "Skilled occupation list",
    publisher: "Department of Home Affairs",
    url: "https://immi.homeaffairs.gov.au/visas/working-in-australia/skill-occupation-list",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Home Affairs 공식 본문에서 visa별 occupation list, ANZSCO 2022·2013 구분, caveat, assessing authority와 변경 영향 확인",
    reviewStatus: "live_verified",
    claimScope:
      "482·186·494 관련 occupation eligibility, ANZSCO code·version, caveat와 skills assessing authority",
    followUp:
      "nomination 직전 current legislative instrument, selected occupation duties와 caveat를 다시 확인",
  },
  {
    sourceId: "employer-sponsored-source-sponsoring",
    topicId: "employer-sponsored-visa",
    title: "Learn about sponsoring",
    publisher: "Department of Home Affairs",
    url: "https://immi.homeaffairs.gov.au/visas/employing-and-sponsoring-someone/sponsoring-workers/learn-about-sponsoring",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Home Affairs 공식 sponsoring page 제목·URL과 2025년 11월 17일 갱신 메타데이터 확인; 상세 구성은 동적 페이지로 제한",
    reviewStatus: "official_index_verified",
    claimScope:
      "employer sponsor·nomination과 worker visa application을 구분하는 공식 sponsorship 안내 시작점",
    followUp:
      "sponsor type, nomination process, labour market testing, levy·cost와 sponsor obligations는 employer application 시점에 직접 확인",
  },
  {
    sourceId: "employer-sponsored-source-worker-rights",
    topicId: "employer-sponsored-visa",
    title: "Workers rights and visa reporting protections",
    publisher: "Department of Home Affairs",
    url: "https://immi.homeaffairs.gov.au/visas/working-in-australia/work-rights-and-exploitation",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Home Affairs 공식 본문에서 동일한 기본 workplace rights, passport 보호, employer의 visa cancellation 권한 부재와 exploitation reporting protection 확인",
    reviewStatus: "live_verified",
    claimScope:
      "temporary visa worker의 exploitation 예방·신고, visa condition 확인과 employer cancellation 협박 대응",
    followUp:
      "reporting protection·Workplace Justice visa pilot의 current eligibility와 certification requirement는 도움 요청 시 직접 확인",
  },
  {
    sourceId: "employer-sponsored-source-fair-work",
    topicId: "employer-sponsored-visa",
    title: "Visa holders and migrants",
    publisher: "Fair Work Ombudsman",
    url: "https://www.fairwork.gov.au/find-help-for/visa-holders-migrants",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Fair Work 공식 본문에서 migrant worker의 동일한 workplace rights, employer visa cancellation 불가, sponsorship payment·cashback 금지와 pay·condition 지원 확인",
    reviewStatus: "live_verified",
    claimScope:
      "고용주 스폰서 worker의 minimum pay·conditions, exploitation·cashback·sponsorship payment와 Fair Work 도움",
    followUp:
      "개별 award·agreement·salary requirement, underpayment remedy와 visa-specific condition은 action 시점에 Fair Work·Home Affairs에서 재확인",
  },
] as const satisfies readonly TopicContentSource[];

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (
  !mutableGuides.some(
    (guide) => guide.topicId === EMPLOYER_SPONSORED_VISA_GUIDE.topicId,
  )
) {
  mutableGuides.push(EMPLOYER_SPONSORED_VISA_GUIDE);
}

const mutableSources = TOPIC_CONTENT_SOURCES as unknown as TopicContentSource[];
for (const source of EMPLOYER_SPONSORED_VISA_SOURCES) {
  if (!mutableSources.some((existing) => existing.sourceId === source.sourceId)) {
    mutableSources.push(source);
  }
}
